import React, { useEffect, useState } from 'react';
import { InputAdornment } from '@mui/material';
import {
  ArcIconButton,
  ArcOutlinedInput,
  ArcPagination,
  ArcStack,
  ArcText,
} from '@src/core/ui';
import { useNavigate } from 'react-router-dom';
import { routesConfig } from '@src/core/routes';
import { ArcBaseSidebar } from '@src/core/components';
import { ReactComponent as ArrowLeftIcon } from '@src/assets/icons/arrow-left.svg';
import { useDispatch, useSelector } from 'react-redux';
import { useGetMissionTemplatesQuery } from '@src/features/order/redux';
import {
  selectSelectedMissionTemplate,
  setSelectedMissionTemplate,
} from '@src/features/order/pages/orders-page/redux';
import { setMapLocation } from '@src/core/controllers';
import { PAGINATION_DEFAULT_SIZE } from '@src/core/config';
import { arcToMapCoordinate } from '@src/core/extensions';
import { ReactComponent as SearchIcon } from '@src/assets/icons/search.svg';
import { ReactComponent as CloseIcon } from '@src/assets/icons/close.svg';
import MissionTemplateCard from './template-card';

const TEMPLATES_LIST_PAGE_SIZE = PAGINATION_DEFAULT_SIZE;
const SEARCH_DEBOUNCE_MS = 350;
const DEFAULT_SORT = ['updatedAt,desc'];

export const MissionTemplatesSidebar: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedTemplate = useSelector(selectSelectedMissionTemplate);
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchPage, setSearchPage] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setSearchPage(0);
  }, [debouncedSearch]);

  const isSearchMode = Boolean(debouncedSearch);
  const listPage = isSearchMode ? searchPage : page;

  const { data: templatesData, isLoading, isFetching } = useGetMissionTemplatesQuery(
    {
      page: listPage,
      size: TEMPLATES_LIST_PAGE_SIZE,
      sort: DEFAULT_SORT,
      ...(isSearchMode ? { search: debouncedSearch } : {}),
    },
    { refetchOnMountOrArgChange: true },
  );

  useEffect(() => {
    return () => {
      dispatch(setSelectedMissionTemplate(null));
    };
  }, [dispatch]);

  const onSelectTemplate = React.useCallback(
    (template: NonNullable<typeof templatesData>['content'][number]) => {
      const dest = template.destinationLocation;
      dispatch(
        setMapLocation({
          latitude: arcToMapCoordinate(dest?.latitude) || 0,
          longitude: arcToMapCoordinate(dest?.longitude) || 0,
          address: dest?.address ?? '',
        }),
      );
      dispatch(setSelectedMissionTemplate(template));
    },
    [dispatch],
  );

  const handlePageChange = React.useCallback(
    (_: unknown, newPage: number) => {
      if (isSearchMode) {
        setSearchPage(newPage - 1);
      } else {
        setPage(newPage - 1);
      }
    },
    [isSearchMode],
  );

  const displayTemplates = templatesData?.content ?? [];
  const totalCount = templatesData?.totalElements ?? 0;
  const totalPages = templatesData?.totalPages ?? 0;
  const currentPage = listPage;
  const showPagination = totalCount > 0 && totalPages > 1;
  const isLoadingList = isLoading;
  const isFetchingList = isFetching;

  useEffect(() => {
    if (isLoadingList || isFetchingList || displayTemplates.length === 0 || selectedTemplate != null) {
      return;
    }
    onSelectTemplate(displayTemplates[0]);
  }, [isLoadingList, isFetchingList, displayTemplates, selectedTemplate, onSelectTemplate]);

  return (
    <ArcBaseSidebar>
      <ArcStack spacing={2} marginTop={0.5} paddingBottom={1.7} sx={{ height: '100%', minHeight: 0 }}>
        <ArcStack direction='row' alignItems='center' spacing={1}>
          <ArcIconButton
            onClick={() => {
              dispatch(setSelectedMissionTemplate(null));
              navigate(routesConfig.orders);
            }}
          >
            <ArrowLeftIcon width={18} />
          </ArcIconButton>
          <ArcText.h5 fontSize={20} fontWeight={500}>
            Mission Templates
          </ArcText.h5>
        </ArcStack>

        <ArcOutlinedInput
          placeholder='Search'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size='small'
          fullWidth
          autoComplete='off'
          sx={{
            '& .MuiInputBase-input': { color: 'white' },
            '& .MuiInputBase-input::placeholder': {
              color: 'text.secondary',
              opacity: 1,
            },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position='start' sx={{ color: 'text.secondary' }}>
                  <SearchIcon style={{ width: 16, height: 16 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position='end' sx={{ color: 'text.secondary' }}>
                  <CloseIcon
                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                    onClick={() => setSearchQuery('')}
                    role='button'
                    aria-label='Clear search'
                  />
                </InputAdornment>
              ) : null,
            },
          }}
        />

        <ArcStack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
          <ArcStack
            spacing={1}
            sx={{
              flex: 1,
              overflowY: 'auto',
              minHeight: 0,
            }}
          >
            {displayTemplates.map((template) => (
              <MissionTemplateCard
                key={template.id}
                template={template}
                isSelected={selectedTemplate?.id === template.id}
                onClick={() => onSelectTemplate(template)}
              />
            ))}

            {displayTemplates.length === 0 && !isLoadingList && (
              <ArcStack alignItems='center' py={4}>
                <ArcText.h6 color='text.secondary'>No mission templates found</ArcText.h6>
              </ArcStack>
            )}

            {isLoadingList && (
              <ArcStack alignItems='center' py={4}>
                <ArcText.h6 color='text.secondary'>Loading templates...</ArcText.h6>
              </ArcStack>
            )}
          </ArcStack>

          {showPagination && (
            <ArcStack alignItems='center' mt={2}>
              <ArcPagination
                page={currentPage + 1}
                count={totalPages}
                onChange={handlePageChange}
              />
            </ArcStack>
          )}
        </ArcStack>
      </ArcStack>
    </ArcBaseSidebar>
  );
};
